import React from "react";
import { LOGO_WIDE_URL } from "./Logo";
import { TIMES, HELV, RED, INK, CREAM, pick, pillarsOf } from "./EditionSheets";

// Social posters (Story 1080×1920 / Post 1200×630) in the final PDF style.
const WHITE = "#FFFFFF";
const T = (style) => ({ fontFamily: TIMES, color: INK, ...style });
const H = (style) => ({ fontFamily: HELV, fontWeight: 700, textTransform: "uppercase", ...style });
const fullName = (d) => [d.name_line1, d.name_line2, d.name_accent].filter(Boolean).join(" ");

function PosterHeader({ s = 1 }) {
  const imgH = 76 * s * 1.7;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 22 * s }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 5 * s }}>
          <div style={{ width: 14 * s, height: 96 * s, background: RED }} />
          <div style={{ width: 14 * s, height: 72 * s, background: INK }} />
        </div>
        <div style={{ height: 76 * s, width: imgH * 2 * 0.9, overflow: "hidden", marginBottom: 6 * s }}>
          <img src={LOGO_WIDE_URL} crossOrigin="anonymous" alt="" style={{ display: "block", maxWidth: "none", height: imgH, width: imgH * 2, marginTop: -imgH * 0.1375, marginLeft: -imgH * 0.1125 }} />
        </div>
      </div>
      <div style={{ height: 11 * s, background: CREAM, marginLeft: 33 * s }} />
    </div>
  );
}

export function CoverStory({ data, lang = "fr" }) {
  if (!data) return null;
  const img = data.cover_design_url || data.cover_photo_url;
  return (
    <div style={{ width: 1080, height: 1920, position: "relative", background: WHITE }}>
      <img src={img} crossOrigin="anonymous" alt="" style={{ position: "absolute", top: 0, left: 0, width: 1080, height: 1620, objectFit: "cover", objectPosition: data.cover_design_url ? "center top" : data.photo_focus || "center 20%" }} />
      <div style={{ position: "absolute", top: 1620, left: 0, right: 0, bottom: 0, borderTop: `10px solid ${RED}`, padding: "44px 70px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 14 }}>
        <div style={H({ fontSize: 26, color: RED, letterSpacing: "0.12em" })}>{pick(data.kicker, lang)} · {pick(data.event_full_title, lang)}</div>
        <div style={T({ fontSize: 58, fontWeight: 700, lineHeight: 1.05 })}>{fullName(data)}</div>
        <div style={H({ fontSize: 24, color: INK, letterSpacing: "0.06em" })}>{pick(data.event_dateline, lang)}</div>
      </div>
    </div>
  );
}

export function CoverPost({ data, lang = "fr" }) {
  if (!data) return null;
  const img = data.cover_design_url || data.cover_photo_url;
  return (
    <div style={{ width: 1200, height: 630, background: WHITE, display: "flex" }}>
      <img src={img} crossOrigin="anonymous" alt="" style={{ width: 420, height: 630, objectFit: "cover", objectPosition: data.cover_design_url ? "center top" : data.photo_focus || "center 20%", flexShrink: 0 }} />
      <div style={{ flex: 1, padding: "44px 56px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <PosterHeader s={0.7} />
        <div>
          <div style={H({ fontSize: 15, color: RED, letterSpacing: "0.3em" })}>{pick(data.kicker, lang)}</div>
          <div style={T({ fontSize: 17, fontWeight: 700, color: RED, textTransform: "uppercase", marginTop: 18 })}>{pick(data.name_title, lang)}</div>
          <div style={T({ fontSize: 62, fontWeight: 700, lineHeight: 0.98, marginTop: 6, textTransform: "uppercase" })}>{fullName(data)}</div>
          <div style={T({ display: "inline-block", marginTop: 16, padding: "6px 14px", background: RED, color: WHITE, fontSize: 26, fontWeight: 700, textTransform: "uppercase" })}>{pick(data.subtitle_italic, lang)}</div>
        </div>
        <div style={H({ fontSize: 15, color: INK, letterSpacing: "0.04em" })}>{pick(data.event_full_title, lang)} · {pick(data.event_dateline, lang)}</div>
      </div>
    </div>
  );
}

export function ArticleStory({ data, lang = "fr" }) {
  if (!data) return null;
  const first = (data.article_sections || [])[0];
  const photo = data.article_photo_url || data.cover_photo_url;
  return (
    <div style={{ width: 1080, height: 1920, background: WHITE, padding: "90px 90px 80px", display: "flex", flexDirection: "column" }}>
      <PosterHeader s={1.1} />
      <div style={H({ fontSize: 28, color: RED, marginTop: 70 })}>{pick(data.article_kicker, lang)}</div>
      <div style={T({ fontSize: 76, fontWeight: 700, lineHeight: 1.1, marginTop: 20 })}>{pick(data.article_title, lang)}</div>
      {photo && <img src={photo} crossOrigin="anonymous" alt="" style={{ width: "100%", height: 640, objectFit: "cover", objectPosition: data.photo_focus || "center 20%", marginTop: 50 }} />}
      {first && (
        <div style={{ marginTop: 50 }}>
          <div style={H({ fontSize: 24, color: RED })}>{pick(first.title, lang)}</div>
          <div style={{ width: 70, height: 4, background: RED, margin: "10px 0 18px" }} />
          <div style={T({ fontSize: 31, lineHeight: 1.42 })}>{pick(first.body, lang)}</div>
        </div>
      )}
      <div style={{ marginTop: "auto", background: CREAM, padding: "28px 34px" }}>
        <div style={T({ fontSize: 30, fontStyle: "italic", lineHeight: 1.3 })}>{pick(data.closing_note, lang)}</div>
        {data.author && <div style={H({ fontSize: 18, color: INK, letterSpacing: "0.2em", marginTop: 14 })}>{lang === "fr" ? "Signé" : "By"} {data.author}</div>}
      </div>
    </div>
  );
}

export function ArticlePost({ data, lang = "fr" }) {
  if (!data) return null;
  const first = (data.article_sections || [])[0];
  const photo = data.article_photo_url || data.cover_photo_url;
  return (
    <div style={{ width: 1200, height: 630, background: WHITE, display: "flex" }}>
      <div style={{ flex: 1, padding: "40px 50px", display: "flex", flexDirection: "column" }}>
        <PosterHeader s={0.62} />
        <div style={H({ fontSize: 14, color: RED, marginTop: 30 })}>{pick(data.article_kicker, lang)}</div>
        <div style={T({ fontSize: 42, fontWeight: 700, lineHeight: 1.1, marginTop: 10 })}>{pick(data.article_title, lang)}</div>
        {first && <div style={T({ fontSize: 17, lineHeight: 1.45, marginTop: 20 })}>{pick(first.body, lang)}</div>}
        <div style={H({ fontSize: 12, color: INK, letterSpacing: "0.2em", marginTop: "auto" })}>{data.author ? `${lang === "fr" ? "Signé" : "By"} ${data.author} · ` : ""}Global Afrique International</div>
      </div>
      {photo && <img src={photo} crossOrigin="anonymous" alt="" style={{ width: 380, height: 630, objectFit: "cover", objectPosition: data.photo_focus || "center 20%", flexShrink: 0 }} />}
    </div>
  );
}

export function ForumStory({ data, lang = "fr" }) {
  if (!data) return null;
  return (
    <div style={{ width: 1080, height: 1920, background: WHITE, padding: "90px 90px 80px", display: "flex", flexDirection: "column" }}>
      <PosterHeader s={1.1} />
      <div style={H({ fontSize: 28, color: RED, marginTop: 80 })}>{pick(data.event_full_title, lang)}</div>
      <div style={T({ fontSize: 104, fontWeight: 700, lineHeight: 1.02, marginTop: 16 })}>{data.event_short_title}</div>
      <div style={T({ fontSize: 50, fontWeight: 700, lineHeight: 1.15, marginTop: 14 })}>{pick(data.event_subtitle_italic, lang)}</div>
      <div style={H({ fontSize: 28, color: INK, marginTop: 24 })}>{pick(data.event_dateline, lang)}</div>
      <div style={{ marginTop: 80, display: "flex", flexDirection: "column", gap: 54 }}>
        {pillarsOf(data, lang).map((p) => (
          <div key={p.n} style={{ display: "flex", gap: 36, alignItems: "flex-start" }}>
            <div style={T({ width: 84, height: 84, background: RED, color: WHITE, fontSize: 44, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })}>{p.n}</div>
            <div>
              <div style={T({ fontSize: 44, fontWeight: 700, color: RED, textTransform: "uppercase" })}>{p.title}</div>
              <div style={T({ fontSize: 30, lineHeight: 1.4, marginTop: 10 })}>{p.body}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: "auto" }}>
        <div style={T({ fontSize: 46, fontWeight: 700, textTransform: "uppercase" })}>Global Afrique International</div>
        <div style={T({ fontSize: 36, fontStyle: "italic", marginTop: 6 })}>{lang === "fr" ? "L'Afrique au cœur du monde." : "Africa at the heart of the world."}</div>
      </div>
    </div>
  );
}

export function ForumPost({ data, lang = "fr" }) {
  if (!data) return null;
  return (
    <div style={{ width: 1200, height: 630, background: WHITE, padding: "38px 54px", display: "flex", flexDirection: "column" }}>
      <PosterHeader s={0.62} />
      <div style={H({ fontSize: 14, color: RED, marginTop: 26 })}>{pick(data.event_full_title, lang)}</div>
      <div style={T({ fontSize: 54, fontWeight: 700, lineHeight: 1.04, marginTop: 6 })}>{data.event_short_title}</div>
      <div style={T({ fontSize: 26, fontWeight: 700, marginTop: 4 })}>{pick(data.event_subtitle_italic, lang)}</div>
      <div style={H({ fontSize: 14, color: INK, marginTop: 8 })}>{pick(data.event_dateline, lang)}</div>
      <div style={{ marginTop: "auto", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 26 }}>
        {pillarsOf(data, lang).map((p) => (
          <div key={p.n} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={T({ width: 40, height: 40, background: RED, color: WHITE, fontSize: 22, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })}>{p.n}</div>
            <div>
              <div style={T({ fontSize: 20, fontWeight: 700, color: RED, textTransform: "uppercase" })}>{p.title}</div>
              <div style={T({ fontSize: 14, lineHeight: 1.4, marginTop: 4 })}>{p.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
